// src/providers/DialogProvider.jsx
import React, { createContext, useContext, useState, useCallback } from "react";
import MsgModal from "../components/UI/DialogModal/MsgModal";
import { errorLog } from "../utils/debugUtils";

// Create Dialog context
const DialogContext = createContext();

/**
 * Global Dialog Provider
 * - Manages the `msgBox` state (confirmation/info/error/etc.)
 * - Renders a single shared MsgModal
 */
export const DialogProvider = ({ children }) => {
	const [msgBox, setMsgBox] = useState(null);

	const clearMsgBox = useCallback(() => setMsgBox(null), []);

	return (
		<DialogContext.Provider value={{ msgBox, setMsgBox, clearMsgBox }}>
			{children}

			{msgBox && (
				<MsgModal
					{...msgBox}
					onCancel={(val) => {
						if (typeof msgBox.cancelCallback === "function") {
							msgBox?.cancelCallback(val);
						}
						clearMsgBox();
					}}
					callback={(val) => {
						if (typeof msgBox.callback === "function") {
							msgBox?.callback(val);
						}
						clearMsgBox();
					}}
				/>
			)}
		</DialogContext.Provider>
	);
};

/**
 * Hook to access dialog state and setter.
 * Should only be used inside a <DialogProvider>.
 */
export const useDialogContext = () => {
	const context = useContext(DialogContext);

	if (!context) {
		errorLog(3, "[DialogProvider][STATE] useDialogContext outside provider", {});

		throw new Error("useDialogContext must be used within a DialogProvider");
	}
	return context;
};
